import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import { ICommodity } from '../commodity/commodity';
import { IOptionType } from '@zz2/zz2-ui';
import OptionType from '../../optionType';
import { IPack } from './pack';
import { IPackFormValues } from './packFormValues';
import { IPackUpsert } from './packUpsert';
import { IPackCategory } from '../packCategory/packCategory';
import { IUnitOfMeasure } from '../unitOfMeasure/unitOfMeasure';
import { ISize } from '../size/size';
import PackBillOfMaterialModelHelper from '../packBillOfMaterial/packBillOfMaterialModelHelper';
import { IMaterial } from '../material/material';

export default class PackModelHelper {
    public static createFormValues = (
        pack : IPack | null,
        packs : Array<IPack>,
        commodities : Array<ICommodity>,
        categories : Array<IPackCategory>,
        sizes : Array<ISize>,
        unitOfMeasures : Array<IUnitOfMeasure>,
        materials : Array<IMaterial>,
        defaultCommodity : ICommodity | null
    ) : IPackFormValues => {
        if (!pack) return {
            id: 0,
            guid: uuidV4(),
            code: '',
            description: '',
            numberOfOuterPackagingOnAPallet: 0,
            outerPackagingWeight: 0,
            outerNettWeight: 0,
            outerGrossWeight: 0,
            hasInnerPack: false,
            numberOfInnersPerOuter: 0,
            innerNettWeight: 0,
            innerPackagingWeight: 0,
            innerGrossWeight: 0,
            numberOfStackedUnits: 0,
            isIntakePack: false,
            sizes: [],
            commodity: defaultCommodity
                ? OptionType.fromDataModel(defaultCommodity)
                : null,
            category: null,
            billOfMaterials: [],
            isActive: true,
        };

        const commodity = commodities.find(x => x.id === pack.commodityId);
        const category = categories.find(x => x.id === pack.categoryId);
        const sizeOptions = sizes.filter(x => pack.sizeIds.includes(x.id))
            .map(OptionType.fromDataModel);

        return {
            id: pack.id,
            guid: pack.guid,
            code: pack.code,
            description: pack.description ?? '',
            numberOfOuterPackagingOnAPallet: pack.noCartons ?? 0,
            outerPackagingWeight: pack.packagingWeight ?? 0,
            outerNettWeight: pack.nettWeight ?? 0,
            outerGrossWeight: pack.grossWeight ?? 0,
            hasInnerPack: pack.hasInnerPack,
            numberOfInnersPerOuter: pack.noUnitsPerCarton,
            innerNettWeight: pack.unitNettWeight,
            innerPackagingWeight: pack.unitPackagingWeight,
            innerGrossWeight: pack.unitGrossWeight,
            numberOfStackedUnits: pack.numberOfStackedUnits,
            isIntakePack: pack.isIntakePack,
            commodity:  commodity
                ? OptionType.fromDataModel(commodity)
                : null,
            category:  category // TODO: OptionType.FromDataModel
                ? { label: `(${category.code}) - ${category.category}`, value: category.id }
                : null,
            sizes: sizeOptions,
            billOfMaterials: pack.billOfMaterials.map(x => PackBillOfMaterialModelHelper.createFormValues(x, packs, materials, unitOfMeasures)),
            isActive: pack.isActive,
        };
    };

    public static createUpsert = (formValues : IPackFormValues) : IPackUpsert => {
        return {
            id: formValues.id,
            guid: formValues.guid,
            code: formValues.code,
            description: formValues.description,
            numberOfOuterPackagingOnAPallet: formValues.numberOfOuterPackagingOnAPallet,
            outerPackagingWeight: formValues.outerPackagingWeight,
            outerNettWeight: formValues.outerNettWeight,
            outerGrossWeight: formValues.outerGrossWeight,
            hasInnerPack: formValues.hasInnerPack,
            numberOfInnersPerOuter: formValues.numberOfInnersPerOuter,
            innerNettWeight: formValues.innerNettWeight,
            innerPackagingWeight: formValues.innerPackagingWeight,
            innerGrossWeight: formValues.innerGrossWeight,
            numberOfStackedUnits: formValues.numberOfStackedUnits,
            isIntakePack: formValues.isIntakePack,
            commodityId: Number(formValues.commodity?.value ?? 0),
            categoryId: Number(formValues.category?.value ?? 0),
            sizeIds: formValues.sizes.map(x => Number(x.value)),
            packBillOfMaterialUpserts: formValues.billOfMaterials
                .filter(x => x.id > 0 || x.isActive) // We do not want to submit new inactive entries (id == 0 && isActive == false).
                .map(x => PackBillOfMaterialModelHelper.createUpsert(x)),
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<IPackFormValues> => Yup.object().shape({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        description: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        numberOfOuterPackagingOnAPallet: Yup.number()
            .typeError('Please enter a number.')
            .min(0, 'Value must be larger than 0')
            .required('Required'),
        outerPackagingWeight: Yup.number()
            .typeError('Please enter a number.')
            .min(0, 'Value must be larger than 0')
            .required('Required'),
        outerNettWeight: Yup.number()
            .typeError('Please enter a number.')
            .min(0, 'Value must be larger than 0')
            .required('Required'),
        outerGrossWeight: Yup.number()
            .typeError('Please enter a number.')
            .min(0, 'Value must be larger than 0')
            .required('Required'),
        numberOfInnersPerOuter: Yup.number()
            .typeError('Please enter a number.')
            .min(0, 'Value must be larger than 0')
            .defined()
            .nullable(),
        innerNettWeight: Yup.number()
            .typeError('Please enter a number.')
            .min(0, 'Value must be larger than 0')
            .defined()
            .nullable(),
        innerPackagingWeight: Yup.number()
            .typeError('Please enter a number.')
            .min(0, 'Value must be larger than 0')
            .defined()
            .nullable(),
        innerGrossWeight: Yup.number()
            .typeError('Please enter a number.')
            .min(0, 'Value must be larger than 0')
            .defined()
            .nullable(),
        numberOfStackedUnits: Yup.number()
            .typeError('Please enter a number.')
            .min(0, 'Value must be larger than 0')
            .defined()
            .nullable(),
        commodity: Yup.mixed<IOptionType>().defined().nullable().required('Required'),
        category: Yup.mixed<IOptionType>().defined().nullable().required('Required'),
        sizes: Yup.array<IOptionType>().defined().required('Required'),
        billOfMaterials: Yup.array(PackBillOfMaterialModelHelper.formSchema()).defined().required('Required'),
        hasInnerPack: Yup.boolean().required('Required'),
        isIntakePack: Yup.boolean().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });
}
